import { versionCount } from '@agavi/blocks';
import { Check, Copy, CopyPlus, Info, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useRecalledHere, useSetTestAnswer } from './answer-ref-context';
import BlockActions from './block-actions';
import { useBlockCopy } from './block-copy-context';
import { useBlockDeleteBlocked } from './block-delete-guard-context';
import { reachableDrivenRows, recallCycle, standInForDrivenRow } from './recall-cycle';
import { useDrivenRowInForce } from './varies-with-section';

import type { Block } from '@/types/experience';

import { IconButton } from '@/components/ui/icon-button';

/**
 * The collapsed block card: its shell, its two rails, and the state classes both depend on.
 *
 * All of it was hand-rolled five times over — once in `collapsed-input-block` for the ten input types,
 * and again in `text`, `image`, `sound` and `dataviz`. The copies had already drifted: three were
 * missing the right rail's rounded corner, and when Duplicate's icon changed, the two shared
 * components moved while the four hand-rolled ones kept the old glyph, so one task could show two
 * different icons for the same action depending on which blocks were in it.
 *
 * One component, therefore.
 */

/** id / type / heritage, on hover or focus. Slated for removal, which is why it sits out of the way. */
export function BlockDebugTooltip({ block }: { block: Block }) {
  const { t } = useTranslation();
  const row = (labelKey: string, value: string) => (
    <div>
      <span className='text-[10px] text-ws-muted'>{t(labelKey)}</span>
      <p className='font-mono text-[11px] text-ws-text break-all'>{value}</p>
    </div>
  );
  return (
    <div className='group/debug relative'>
      <button
        type='button'
        aria-label={t('experience.debug.title')}
        className='cursor-default rounded-sm opacity-40 hover:opacity-100 focus-visible:opacity-100 focus-visible:[outline:2px_auto]'
      >
        <Info aria-hidden='true' className='h-3.5 w-3.5 text-ws-muted' />
      </button>
      {/* Anchored BELOW its trigger now that the trigger sits at the top of the rail — the old
          `bottom-0` would have hung the panel off the top edge of the card. */}
      <div className='pointer-events-none absolute left-full top-0 z-50 hidden w-56 rounded-md border border-ws-border bg-ws-surface p-2.5 shadow-md group-hover/debug:block group-focus-within/debug:block'>
        <p className='mb-1.5 text-[10px] font-medium uppercase tracking-wide text-ws-muted'>{t('experience.debug.title')}</p>
        <div className='space-y-1'>
          {row('experience.debug.id', block.id)}
          {row('experience.debug.type', block.type)}
          {row('experience.debug.heritage', block.heritageId ?? t('experience.debug.original'))}
        </div>
      </div>
    </div>
  );
}

/**
 * Left rail: debug at the top, the block's own icon in the middle, grading mode at the bottom.
 *
 * Grading mode sits at the BOTTOM deliberately, level with the answer bar that runs along the
 * bottom of the middle column. It says how that bar's answer is treated, and two rows away from
 * the thing it qualifies it was an icon with no referent. Debug takes the top because it is the one
 * that is leaving.
 */
export function BlockCardLeftRail({ block, icon, sidebarBg, gradingMode }: {
  block: Block;
  icon: React.ReactNode;
  sidebarBg: string;
  /** Content blocks have none; a spacer keeps their icon on the same centre line as everyone's. */
  gradingMode?: React.ReactNode;
}) {
  const { t } = useTranslation();
  /*
   * DERIVED HERE, not passed in. Every block type renders its own rail call, so a `versionCount`
   * prop would be fourteen chances to forget one — and the badge is exactly the kind of thing
   * nobody notices is missing. The rail already has the block, so it can ask.
   */
  const versions = versionCount(block.data as { versions?: unknown[] });
  return (
    <div className={`flex w-10 shrink-0 flex-col items-center rounded-l-xl border-r border-ws-border py-2 ${sidebarBg}`}>
      <BlockDebugTooltip block={block} />
      {/*
        * BENEATH the symbol, not a corner badge on it.
        *
        * A filled pill in the top-right corner is the unread-notification idiom, and a teacher
        * trained by every other app reads it as something to clear. A bank is a standing property
        * of the question, so it must not borrow that shape. Sitting under the symbol it overlaps
        * nothing, which also means it works unchanged on all fourteen block glyphs — `SquareCheck`
        * and `SquareChevronDown` fill their box edge to edge and would have taken a knockout ring
        * to survive a corner.
        *
        * `×4`, not `4`: the rail also carries a grading glyph, so a bare numeral invites "four
        * what?". The multiplication sign says "four of these" and needs no translation.
        */}
      <div className='flex flex-1 flex-col items-center justify-center gap-0.5'>
        {icon}
        {versions > 0 && (
          <span
            aria-hidden='true'
            className='text-[10.5px] font-semibold leading-none tabular-nums text-ws-primary dark:text-ws-text-link'
          >
            {`×${versions}`}
          </span>
        )}
      </div>
      {versions > 0 && <span className='sr-only'>{t('blocks.versions.count', { count: versions })}</span>}
      {gradingMode ?? <div aria-hidden='true' className='h-3.5 w-3.5' />}
    </div>
  );
}

/**
 * Right rail: Copy and Duplicate together at the top, Delete alone at the bottom.
 *
 * Copy belongs beside Duplicate — they are one gesture at two scopes, "take this block" and "take
 * this block and put it right there", which is why the icons are the same shape. Delete stays at
 * the far end, where a two-click armed control is not somewhere a hand aiming at Copy can land.
 */
export function BlockCardActionRail({
  block, isDeleteArmed, sidebarBg, onDuplicate, onDelete, onDisarm,
}: {
  block: Block;
  isDeleteArmed: boolean;
  sidebarBg: string;
  onDuplicate: () => void;
  onDelete: () => void;
  onDisarm: () => void;
}) {
  const { t } = useTranslation();
  const clipboard = useBlockCopy();
  // Copy puts nothing on the page — the block stays exactly as it was — so the only evidence the
  // press landed is the button itself briefly becoming a tick. Same idiom as every other copy
  // button anyone has used, and it appears exactly where the finger was.
  const justCopied = clipboard?.justCopiedId === block.id;
  /** The rail's half of the guard. Present in BOTH layouts or it is not a guard — the same
   *  argument `block-copy-context` makes for Copy. */
  const deleteBlocked = useBlockDeleteBlocked(block.id);
  return (
    <div className={`flex w-10 shrink-0 flex-col items-center justify-between gap-3 rounded-r-xl border-l border-ws-border py-2 ${sidebarBg}`}>
      <div aria-live='assertive' aria-atomic='true' className='sr-only'>
        {isDeleteArmed ? t('blocks.actions.deleteArmed') : ''}
      </div>
      <div className='flex flex-col items-center gap-1'>
        {clipboard && (
          <IconButton
            size='sm'
            variant='primary'
            onPointerDown={(e) => { if (e.button !== 0) return; e.stopPropagation(); clipboard.copy(block.id); }}
            onClick={(e) => { e.stopPropagation(); if (e.detail === 0) clipboard.copy(block.id); }}
            aria-label={justCopied ? t('task.paste.copied') : t('task.paste.copyBlock')}
          >
            {justCopied
              ? <Check className='h-4 w-4 text-ws-success-fg' />
              : <Copy className='h-4 w-4' />}
          </IconButton>
        )}
        <IconButton
          size='sm'
          variant='primary'
          onPointerDown={(e) => { if (e.button !== 0) return; e.stopPropagation(); onDuplicate(); }}
          onClick={(e) => { e.stopPropagation(); if (e.detail === 0) onDuplicate(); }}
          aria-label={t('blocks.actions.duplicate')}
        >
          <CopyPlus className='h-4 w-4' />
        </IconButton>
      </div>
      <IconButton
        size='sm'
        variant='destructive'
        isArmed={isDeleteArmed}
        disabled={!!deleteBlocked}
        onPointerDown={(e) => { if (e.button !== 0) return; e.stopPropagation(); onDelete(); }}
        onPointerLeave={(e) => { if (isDeleteArmed && e.pointerType !== 'touch') onDisarm(); }}
        onBlur={() => { if (isDeleteArmed) onDisarm(); }}
        onClick={(e) => { e.stopPropagation(); if (e.detail === 0) onDelete(); }}
        title={deleteBlocked}
        aria-label={deleteBlocked ?? (isDeleteArmed ? t('blocks.actions.confirmDelete') : t('blocks.actions.delete'))}
      >
        <Trash2 className='h-4 w-4' />
      </IconButton>
    </div>
  );
}

/** `bg-ws-hover`, or the warning tint while delete is armed. Shared so the rails and the expanded
 * header can never disagree about what "armed" looks like. */
export function cardSidebarBg(isDeleteArmed: boolean): string {
  return isDeleteArmed ? 'bg-ws-warning-bg' : 'bg-ws-hover';
}

/**
 * The card's border for its current state, in priority order.
 *
 * The union of what the five hand-rolled copies each did: only the input card passes
 * `testBorder` (the live-test evaluation colour), so every caller gets exactly the behaviour it
 * had, out of one expression instead of five that could drift.
 *
 * There was a `previewMode` dashed border too, passed by the text card alone for a private
 * eye-button mode that predated the active-vs-display convention. Selecting a block is the
 * trigger now, so the mode went and this went with it.
 */
export function cardBorderClass(state: {
  isDeleteArmed: boolean;
  isSelected?: boolean;
  isDragOver?: boolean;
  testBorder?: string | null;
}): string {
  if (state.isDeleteArmed) return 'border border-ws-warning-fg [outline:1px_solid_var(--color-ws-warning-fg)]';
  if (state.isSelected && state.testBorder) return state.testBorder;
  if (state.isSelected) return 'border border-ws-primary [outline:2px_solid_var(--color-ws-primary)] [outline-offset:-1px]';
  if (state.isDragOver) return 'border border-ws-primary';
  return 'border border-ws-border';
}

/**
 * The whole collapsed card: shell, both rails, and the block's own content between them.
 *
 * Extracting only the rails was not enough — five shells remained, and they had already drifted
 * (three carried `overflow-hidden` and two did not). That difference is not cosmetic: the debug
 * tooltip escapes its rail horizontally, so a card that clips its overflow clips the tooltip. The
 * clipping media blocks actually need belongs on their own middle column, around the image or the
 * chart, not around the rails.
 *
 * A block supplies its middle column as `children` and nothing else about the card.
 */
export function BlockCard({
  block, cardRef, icon, gradingMode, rails = true,
  isDeleteArmed, isSelected, isDragOver, testBorder,
  onDuplicate, onDelete, onDisarm, children,
}: {
  block: Block;
  cardRef: React.Ref<HTMLDivElement>;
  icon: React.ReactNode;
  gradingMode?: React.ReactNode;
  /** Hidden while the card is the live test surface — its own controls own the space then. */
  rails?: boolean;
  isDeleteArmed: boolean;
  isSelected?: boolean;
  isDragOver?: boolean;
  testBorder?: string | null;
  onDuplicate: () => void;
  onDelete: () => void;
  onDisarm: () => void;
  children: React.ReactNode;
}) {
  const sidebarBg = cardSidebarBg(isDeleteArmed);
  const onPickRecall = useRecallPickHandler(isSelected ? block.id : undefined);
  return (
    <div
      ref={cardRef}
      /* `hover-card` is tier 2: a border tint, never a lift. These sit in a dense column and a
         moving card would fight the per-control hovers inside it. */
      className={`hover-card flex rounded-xl bg-ws-surface ${cardBorderClass({ isDeleteArmed, isSelected, isDragOver, testBorder })}`}
      /* BOTH cards, because a block draws one or the other and the canvas draws THIS one —
         see `useRecallPickHandler`. Wiring only the panel left the gesture working in the
         editor beside the card and dead on the card itself, which is where it is used.
         Gated on selection because that is the only state that renders a value to pick: a
         block mounts its `BlockPreviewProvider` when selected, so an unselected card holds
         chips, not controls, and sampling answers for it would be work with nothing to show
         for it. */
      onClickCapture={onPickRecall}
      onClick={() => { if (isDeleteArmed) onDisarm(); }}
    >
      {rails && <BlockCardLeftRail block={block} icon={icon} sidebarBg={sidebarBg} gradingMode={gradingMode} />}
      {children}
      {rails && (
        <BlockCardActionRail
          block={block}
          isDeleteArmed={isDeleteArmed}
          sidebarBg={sidebarBg}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
          onDisarm={onDisarm}
        />
      )}
    </div>
  );
}

/**
 * The other card: a header bar instead of rails.
 *
 * This is what a block draws when it is not a collapsed canvas card — the embedded editor panel and
 * the content blocks' expanded state. It was hand-rolled the same five times as the rails card, and
 * every copy was byte-identical apart from the icon and the title, right down to the class list.
 *
 * `BlockActions` is the horizontal sibling of the action rail. The two exist because one layout is
 * a row and the other a column, but they carry the same buttons and now read their state classes
 * from the same two functions, so Duplicate cannot change shape in one and not the other again.
 */
export function BlockPanelCard({
  blockId, cardRef, icon, title, header = true,
  isDeleteArmed, isSelected, isDragOver, testBorder,
  onDuplicate, onDelete, onDisarm, children,
}: {
  blockId?: string;
  cardRef: React.Ref<HTMLDivElement>;
  icon: React.ReactNode;
  title: React.ReactNode;
  /** Hidden while the card is the live test surface. */
  header?: boolean;
  isDeleteArmed: boolean;
  isSelected?: boolean;
  isDragOver?: boolean;
  testBorder?: string | null;
  onDuplicate: () => void;
  onDelete: () => void;
  onDisarm: () => void;
  children: React.ReactNode;
}) {
  const onPickRecall = useRecallPickHandler(isSelected ? blockId : undefined);
  return (
    <div
      ref={cardRef}
      className={`group hover-card relative rounded-xl bg-ws-surface ${cardBorderClass({ isDeleteArmed, isSelected, isDragOver, testBorder })}`}
      onClickCapture={onPickRecall}
      onClick={() => { if (isDeleteArmed) onDisarm(); }}
    >
      {header && (
        <div className={`flex items-center gap-2 rounded-t-xl border-b border-ws-border px-4 py-2.5 text-base font-semibold text-ws-primary dark:text-ws-text-link ${cardSidebarBg(isDeleteArmed)}`}>
          {icon}
          <span className='flex-1'>{title}</span>
          <BlockActions
            blockId={blockId}
            isDeleteArmed={isDeleteArmed}
            onTriggerDuplicate={onDuplicate}
            onTriggerDelete={onDelete}
            onDisarm={onDisarm}
          />
        </div>
      )}
      {children}
      {/*
        * THE REFERENCE STAND-INS, on every card that has one to set.
        *
        * It lived in the input-block frame first, which put it on exactly the blocks whose own
        * answer is the point and left it off the ones that mostly QUOTE an answer — a Text
        * block carrying "you said {velocity}" is the commonest reference there is, and it had
        * no way to ask for a different one.
        *
        * Here instead, because every block type ends up in this card and none of them knows
        * anything about which answers its prose quotes that this does not. Last in the card,
        * under whatever the block put in `children`, so it reads as bench furniture rather
        * than as part of the block.
        */}
    </div>
  );
}

/**
 * CLICKING A QUOTED ANSWER TAKES THE NEXT ONE.
 *
 * The value rendered into the prose is a button carrying `data-answer-ref-pick`, and it is an
 * html string, so there is no React handler to attach — one delegated listener here catches
 * every copy of it, however many places the block quotes the same answer.
 *
 * CAPTURE, not bubble. The prose a value sits in stops the click on its own way up — a card
 * opens its editor when you click it, so every block swallows presses inside its content — and
 * a listener waiting at the top would never hear this one. Capture runs outside-in, so the
 * card sees it first and spends it.
 *
 * CYCLES rather than opening a menu. The common source has two answers, where a menu is three
 * interactions to do what one click does, and the bar below states the same setting once for
 * anything longer. Wrapping round is what makes it a cycle rather than a dead end at the last
 * answer.
 *
 * Only answers with a value to give are in the cycle: an open class ("anything else") has
 * none until there is a control that takes a typed one, and cycling onto it would blank the
 * sentence.
 */
export function useRecallPickHandler(blockId: string | undefined): (e: React.MouseEvent) => void {
  const rows = useRecalledHere(blockId);
  const setTestAnswer = useSetTestAnswer();
  /*
   * THE DRIVEN ENTRY CYCLES THE AXIS'S ROWS, not the driver's answer choices.
   *
   * "No answer" is a row with a version of its own and is in no list of answers, so a cycle
   * built from the choices paged three of an MCQ's four versions — and the editor's pager,
   * which walks the rows, would then have offered four. Two controls over one pointer
   * disagreeing about how many positions there are is the defect this feature keeps
   * producing; they read the same list here.
   *
   * Only the DRIVEN entry. An ordinary chip quoting an answer still skips a class with no
   * value to give: cycling a sentence onto "no answer" blanks the word it is standing in.
   */
  const driven = useDrivenRowInForce();
  return (e: React.MouseEvent) => {
    const hit = (e.target as HTMLElement | null)?.closest?.('[data-answer-ref-pick]');
    if (!hit || !setTestAnswer) return;
    // The card underneath opens things on click; this press is spent here.
    e.stopPropagation();
    e.preventDefault();
    const aliasId = hit.getAttribute('data-answer-ref-pick')!;
    const row = rows.find(r => r.aliasId === aliasId);
    if (!row) return;
    if (row.driven && driven) {
      /* `standInForDrivenRow`, shared with the editor's pager — one list, so the card and the
         box cannot disagree about which rows exist. Wrapping, because one press forward is a
         cycle; the editor's arrows are a position and stop at the ends. */
      const reachable = reachableDrivenRows(driven.rows, row.choices, row.source);
      if (reachable.length === 0) return;
      const at = Math.max(0, reachable.findIndex(r => r.id === driven.row.id));
      const next = standInForDrivenRow(reachable[(at + 1) % reachable.length]!, row.choices, row.source);
      if (next) setTestAnswer(aliasId, next.sample);
      return;
    }
    /* `recallCycle`, shared with the editor's driven pager — see its own note on why the two
       controls cannot each work out where the cycle is standing. */
    const { usable, at } = recallCycle(row);
    if (usable.length === 0) return;
    setTestAnswer(aliasId, usable[(at + 1) % usable.length]!.sample);
  };
}

